import React, { useEffect, useState, useMemo } from 'react';
import { MDBPagination, MDBPageItem, MDBPageNav } from 'mdbreact';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { callbackSvgs } from 'assets/icons';

import FilterField from 'components/FormGroup/FilterField';
import SelectField from 'components/FormGroup/SelectField';

import ContentLoader from 'components/Informations/Loaders/ContentLoader';
import FilledButton from 'components/Buttons/FilledButton';
import InformationCard from 'components/Cards/InformationCard';

import If from 'components/If';
import Page from 'components/PageStructure';
import Toast from 'components/Informations/Toast';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import ProjectLine from './InternalComponents/ProjectLine';

import * as S from './styles';

const { getProjects } = endpoints;

export default function CoordinateProjects({ history, user, location }) {
  const { state } = location;
  const { id_funcionario: idFuncionario, administrador, name } = user;
  const isAdmin = administrador === 1;

  const [isLoading, setIsLoading] = useState(true);
  const [currentInputSearch, setCurrentInputSearch] = useState('');
  const [currentSelectSearch, setCurrentSelectSearch] = useState('');

  const [projectsToSearch, setProjectsToSearch] = useState([]);

  const [toastVisibility, setToastVisiblity] = useState(true);
  const [toastType, setToastType] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastTime, setToastTime] = useState(2000);

  const [projects, setProjects] = useState([]);
  const [paginationIndex, setPaginationIndex] = useState(1);
  const [numOfPages, setNumOfPages] = useState([]);

  const [paginatedProjects] = useFetch(`${getProjects}/all/${paginationIndex}`);
  const [allProjects] = useFetch(`${getProjects}/all`);
  const [projectCounter, setProjectCounter] = useState(0);

  const handleInputSearch = () => {
    const filterCondition = key =>
      key.toLowerCase().indexOf(currentInputSearch.toLowerCase()) >= 0;

    const filteredProjects = projectsToSearch?.filter(
      ({ nome, identificacao }) =>
        filterCondition(nome) || filterCondition(identificacao),
    );

    return (
      <ProjectLine
        projects={filteredProjects}
        paginationIndex={paginationIndex}
      />
    );
  };

  const handleSelectSearch = value => {
    const selectedProjects = projectsToSearch?.filter(
      ({ status_projeto: status }) => status === value,
    );

    return (
      <ProjectLine
        projects={selectedProjects}
        paginationIndex={paginationIndex}
      />
    );
  };

  useEffect(() => {
    if (state) {
      setPaginationIndex(state);
    }
  }, [state]);

  useEffect(() => {
    (async () => {
      const filteredProjects = allProjects?.filter(
        item => item.id_func_gp_smtx === idFuncionario,
      ).length;

      const projectsCount = allProjects?.length;

      const paginationArr =
        allProjects &&
        Array.from(
          { length: projectsCount / 10 + (projectsCount % 10 ? 1 : 0) },
          y => y + 1,
        );

      const orderedProjects =
        paginatedProjects?.sort((a, b) => (a.nome > b.nome ? 1 : -1)) ?? [];

      const cbFilter = item => item.id_func_gp_smtx === idFuncionario;

      const managerProjects = allProjects?.filter(cbFilter) ?? [];

      const projectByUser = isAdmin ? orderedProjects : managerProjects;

      const isLoaded =
        !!allProjects.length &&
        !!paginatedProjects.length &&
        !!paginationArr.length;

      setProjects(projectByUser || []);
      setNumOfPages(paginationArr || []);
      setProjectCounter(filteredProjects || 0);
      setProjectsToSearch(isAdmin ? allProjects : managerProjects);
      setIsLoading(!isLoaded);
    })();
  }, [paginatedProjects, allProjects, idFuncionario, isAdmin, name]);

  useEffect(() => {
    const toast =
      localStorage.getItem('successToast') !== null || undefined
        ? JSON.parse(localStorage.getItem('successToast'))
        : {};

    if (Object.keys(toast).length) {
      const {
        toastType: type,
        toastMessage: message,
        toastCloseTime: closeTime,
      } = toast;

      setToastVisiblity(true);
      setToastTime(closeTime);
      setToastMessage(message);
      setToastType(type);

      setTimeout(() => {
        setToastVisiblity(false);
        setToastMessage('');
        setToastType('');
      }, closeTime);
    }

    return () => {
      setToastTime(2000);
      setToastMessage('');
      setToastType('');
      localStorage.removeItem('successToast');
    };
  }, []);

  const statusOptions = useMemo(() => {
    const allStatus = _.uniqBy(
      projectsToSearch.map(({ status_projeto: status }) => ({
        value: status,
        label: status,
      })),
      'label',
    );

    return currentSelectSearch
      ? [{ value: 0, label: 'Limpar filtro' }, ...allStatus]
      : [...allStatus];
  }, [projectsToSearch, currentSelectSearch]);

  return (
    <>
      {!projects.length && isLoading && (
        <S.Loader>
          <ContentLoader />
        </S.Loader>
      )}

      {toastVisibility && (
        <Toast
          type={toastType}
          message={toastMessage}
          autoClose={toastTime}
          hideProgress
        />
      )}

      <Page
        title="Organize e gerencie seus projetos"
        subtitle="Aqui você consegue realizar a gerência de todos os projetos que estão sob sua responsabilidade."
      >
        <S.ProjectInfos>
          <InformationCard
            counter={projectCounter}
            description="Projetos coordenados"
          />
        </S.ProjectInfos>

        <S.ProjectActions>
          <div>
            <S.FormGroup>
              <FilterField
                handleChange={e => setCurrentInputSearch(e.target.value)}
                value={currentInputSearch}
                name="filterProjects"
                labelText="Deseja pesquisar um projeto em específico?"
                width={400}
                placeholderText="Insira nome ou identificação"
                noTransparency={false}
                isDisabled={isLoading}
              />
            </S.FormGroup>
            <S.FormGroup>
              <SelectField
                hasSecondaryStyle
                labelText="Pesquise pelo status do projeto"
                placeholderText="Selecione um status..."
                options={statusOptions}
                value={statusOptions.find(
                  ({ value }) => value === currentSelectSearch,
                )}
                handleChange={({ value }) => {
                  handleSelectSearch(value);
                  setCurrentSelectSearch(value);
                }}
                isDisabled={isLoading || currentInputSearch}
              />
            </S.FormGroup>
          </div>
          {isAdmin && (
            <FilledButton
              text="+ Adicionar projeto"
              handleClick={() =>
                history.push(
                  '/auth/coordinate-projects/add-project',
                  paginationIndex,
                )
              }
            />
          )}
        </S.ProjectActions>

        <S.Projects>
          <If condition={projects.length <= 0 && !isLoading}>
            <S.Callback>
              {callbackSvgs('interrogationWoman')}
              <S.CallbackText>
                <p>Atualmente você não está coordenando nenhum projeto</p>
              </S.CallbackText>
            </S.Callback>
          </If>

          <If condition={currentSelectSearch && !currentInputSearch}>
            {handleSelectSearch(currentSelectSearch)}
          </If>

          <If condition={currentInputSearch}>{handleInputSearch()}</If>

          <If condition={!currentInputSearch && !currentSelectSearch}>
            <ProjectLine
              projects={projects}
              paginationIndex={paginationIndex}
            />
          </If>
        </S.Projects>

        <If
          condition={
            projects.length > 0 &&
            !currentInputSearch &&
            !currentSelectSearch &&
            numOfPages.length > 0 &&
            isAdmin
          }
        >
          <MDBPagination
            size="lg"
            className="d-inline-flex justify-content-center mb-3 mt-0 center w-100"
            color="blue"
          >
            <MDBPageItem disabled={paginationIndex === 1}>
              <MDBPageNav
                aria-label="Previous"
                className="page-link"
                onClick={() => setPaginationIndex(1)}
              >
                <span>Primeira</span>
              </MDBPageNav>
            </MDBPageItem>

            <MDBPageItem disabled={paginationIndex === 1}>
              <MDBPageNav
                onClick={() => setPaginationIndex(prev => prev - 1)}
                className="page-link"
              >
                <span aria-hidden="true">&laquo;</span>
              </MDBPageNav>
            </MDBPageItem>

            <MDBPageItem>
              <MDBPageNav>
                {paginationIndex}
                <span className="sr-only">(current)</span>
              </MDBPageNav>
            </MDBPageItem>

            <MDBPageItem disabled={paginationIndex >= numOfPages.length}>
              <MDBPageNav
                onClick={() => setPaginationIndex(prev => prev + 1)}
                className="page-link"
              >
                <span aria-hidden="true">&raquo;</span>
              </MDBPageNav>
            </MDBPageItem>

            <MDBPageItem disabled={paginationIndex >= numOfPages.length}>
              <MDBPageNav
                aria-label="Next"
                onClick={() => setPaginationIndex(numOfPages.length)}
                className="page-link"
              >
                <span>Última</span>
              </MDBPageNav>
            </MDBPageItem>
          </MDBPagination>
        </If>
      </Page>
    </>
  );
}

CoordinateProjects.defaultProps = {
  location: {},
  history: {},
  user: {},
};

CoordinateProjects.propTypes = {
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
